import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLocale } from '@parentpath/i18n';
import type { MilestoneWithTracking, BabyInfo } from '../types';

export interface UseMilestonesResult {
  milestones: MilestoneWithTracking[];
  babies: BabyInfo[];
  isLoading: boolean;
  error: Error | null;
  checkMilestone: (params: {
    babyIndex: number;
    milestoneId: string;
    notes?: string;
  }) => Promise<void>;
  uncheckMilestone: (params: {
    babyIndex: number;
    milestoneId: string;
  }) => Promise<void>;
  updateNotes: (params: {
    babyIndex: number;
    milestoneId: string;
    notes: string;
  }) => Promise<void>;
  isUpdating: boolean;
}

export function useMilestones(phaseId: string | undefined): UseMilestonesResult {
  const { user } = useAuthStore();
  const { lastCompletedPregnancy } = usePregnancyStore();
  const { locale } = useLocale();
  const queryClient = useQueryClient();

  const pregnancyId = lastCompletedPregnancy?.id;

  // Extract babies from pregnancy data
  const babies: BabyInfo[] = [];
  if (lastCompletedPregnancy?.baby_data?.babies) {
    lastCompletedPregnancy.baby_data.babies.forEach((baby, index) => {
      babies.push({
        index,
        name: baby.name || `Baby ${index + 1}`,
        gender: (baby.gender === 'male' || baby.gender === 'female' ? baby.gender : 'neutral') as 'male' | 'female' | 'neutral',
      });
    });
  } else if (lastCompletedPregnancy?.baby_names && Array.isArray(lastCompletedPregnancy.baby_names)) {
    lastCompletedPregnancy.baby_names.forEach((name: string | { name: string; gender?: string }, index: number) => {
      const babyName = typeof name === 'string' ? name : name.name;
      const gender = typeof name === 'object' && name.gender ? name.gender : 'neutral';
      babies.push({
        index,
        name: babyName || `Baby ${index + 1}`,
        gender: (gender === 'male' || gender === 'female' ? gender : 'neutral') as 'male' | 'female' | 'neutral',
      });
    });
  }

  // If no babies found, default to one baby
  if (babies.length === 0) {
    babies.push({ index: 0, name: 'Baby', gender: 'neutral' });
  }

  // Fetch milestones with tracking status
  const { data: milestones = [], isLoading, error } = useQuery({
    queryKey: ['baby-milestones', phaseId, pregnancyId, locale],
    queryFn: async () => {
      if (!phaseId || !pregnancyId) return [];

      // 1. Fetch milestones for this phase
      const { data: milestonesData, error: milestonesError } = await supabase
        .from('baby_development_milestones')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', locale)
        .eq('is_active', true)
        .order('sort_order');

      if (milestonesError) throw milestonesError;
      if (!milestonesData || milestonesData.length === 0) return [];

      // 2. Fetch tracking status for all babies
      const milestoneIds = milestonesData.map(m => m.id);
      const { data: trackingData, error: trackingError } = await supabase
        .from('user_baby_milestones')
        .select('*')
        .eq('pregnancy_id', pregnancyId)
        .in('milestone_id', milestoneIds);

      if (trackingError) throw trackingError;

      // 3. Combine milestones with tracking
      return milestonesData.map((milestone): MilestoneWithTracking => ({
        ...milestone,
        baby_tracking: babies.reduce((acc, baby) => {
          const tracking = trackingData?.find(
            t => t.milestone_id === milestone.id && t.baby_index === baby.index
          );
          acc[baby.index] = tracking ?? null;
          return acc;
        }, {} as Record<number, any>),
      }));
    },
    enabled: !!phaseId && !!pregnancyId,
  });

  // Check milestone mutation
  const checkMutation = useMutation({
    mutationFn: async ({
      babyIndex,
      milestoneId,
      notes,
    }: {
      babyIndex: number;
      milestoneId: string;
      notes?: string;
    }) => {
      if (!pregnancyId || !user?.id) throw new Error('Not authenticated');

      const insertData = {
        pregnancy_id: pregnancyId,
        baby_index: babyIndex,
        milestone_id: milestoneId,
        checked_by: user.id,
        notes: notes || null,
        notes_by: notes ? user.id : null,
        notes_at: notes ? new Date().toISOString() : null,
      };

      console.log('[Milestone] Inserting:', insertData);

      const { error } = await supabase
        .from('user_baby_milestones')
        .insert(insertData);

      if (error) {
        console.error('[Milestone] Insert error:', error);
        throw new Error(error.message || 'Failed to save milestone');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-milestones'] });
    },
  });

  // Uncheck milestone mutation
  const uncheckMutation = useMutation({
    mutationFn: async ({
      babyIndex,
      milestoneId,
    }: {
      babyIndex: number;
      milestoneId: string;
    }) => {
      if (!pregnancyId) throw new Error('No pregnancy');

      const { error } = await supabase
        .from('user_baby_milestones')
        .delete()
        .eq('pregnancy_id', pregnancyId)
        .eq('baby_index', babyIndex)
        .eq('milestone_id', milestoneId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-milestones'] });
    },
  });

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async ({
      babyIndex,
      milestoneId,
      notes,
    }: {
      babyIndex: number;
      milestoneId: string;
      notes: string;
    }) => {
      if (!pregnancyId || !user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_baby_milestones')
        .update({
          notes,
          notes_by: user.id,
          notes_at: new Date().toISOString(),
        })
        .eq('pregnancy_id', pregnancyId)
        .eq('baby_index', babyIndex)
        .eq('milestone_id', milestoneId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-milestones'] });
    },
  });

  return {
    milestones,
    babies,
    isLoading,
    error: error as Error | null,
    checkMilestone: checkMutation.mutateAsync,
    uncheckMilestone: uncheckMutation.mutateAsync,
    updateNotes: updateNotesMutation.mutateAsync,
    isUpdating: checkMutation.isPending || uncheckMutation.isPending || updateNotesMutation.isPending,
  };
}
